import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  IconButton,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getSortedRowModel,
  type Table as ReactTable,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { updateRequestFileReferences as apiUpdateFileRefs } from '~/api/civilsearch';
import type { CivilGridFile, DataRequestV2 } from '~/common/types';
import { Notify } from '~/components2/Notify';
import Table from '~/components2/Table';
import store from '../../../store';
import evidenceStore from '../store';
import { columns } from './columns';

interface Props {
  file: CivilGridFile;
  parentTable: ReactTable<CivilGridFile>;
}

export default function LinkRequest({ file, parentTable }: Props) {
  const { requestId } = useParams({ strict: false }) as { requestId: string };
  const { project } = useSnapshot(store) as typeof store;
  const { projectRequests } = useSnapshot(evidenceStore) as typeof evidenceStore;

  const data = useMemo(() => {
    return Object.values(projectRequests);
  }, [projectRequests]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const initialSelection: RowSelectionState = useMemo(() => {
    const selection: RowSelectionState = {};

    for (const key in projectRequests) {
      const r = projectRequests[key];
      const f = r.files.find((_f) => _f.id === file.id);
      if (f) {
        selection[r.id] = true;
      }
    }

    return selection;
  }, [projectRequests]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (r) => r.id,
    initialState: {
      sorting: [{ id: 'agencyName', desc: false }],
      rowSelection: initialSelection,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const isDisabled = useMemo(() => {
    const selection = Object.keys(table.getSelectedRowModel().rowsById);
    const initial = Object.keys(initialSelection);
    return isEqual(selection, initial);
  }, [table.getSelectedRowModel().rowsById, initialSelection]);

  const onSubmit = async () => {
    try {
      if (isDisabled) return;
      const selection = Object.keys(table.getSelectedRowModel().rowsById);
      const res = await apiUpdateFileRefs(project!.id, file.id, selection);
      if (!res.success) throw new Error('Error');

      parentTable?.options.meta?.refreshData?.();
      Notify.info({ message: 'File links updated' });
    } catch (e) {
      console.error('Error updating file links: ', e);
      Notify.error({ message: 'An error occurred while updating file links' });
    }
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        // on close, reset the row selection to original state
        if (!open) {
          table.resetRowSelection();
        }
      }}
    >
      <DialogTrigger asChild>
        <IconButton
          variant="hollow"
          icon="component1"
          aria-label={`Link requests to ${file.name}`}
          data-testid={`evidence-file-link-requests-${file.id}`}
        />
      </DialogTrigger>
      <DialogContent
        title={`Link Requests to File - ${file.name}`}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`evidence-file-link-requests-dialog-${file.id}`}
      >
        <DialogBody className="text-sm">
          <div className="relative flex w-full flex-col space-y-4 overflow-clip">
            <div>
              Current Request ID: <span className="font-bold">{projectRequests[requestId]?.requestId}</span>
            </div>
            <div>
              Select data requests for the project{' '}
              {project?.name ? <span className="font-bold">{project.name}</span> : 'the project'} to link this file to.
            </div>

            <Warning table={table} />

            <div className="max-h-[40vh] w-full grow overflow-y-auto">
              <Table id="cs-evidence-links-v1" table={table} />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            disabled={isDisabled}
            onClick={onSubmit}
            data-testid={`evidence-file-link-requests-confirm-${file.id}`}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface WarningProps {
  table: ReactTable<DataRequestV2>;
}

const Warning = ({ table }: WarningProps) => {
  const hasSelection = table.getSelectedRowModel().rows.length;
  if (hasSelection) return null;

  return (
    <div className="rounded-xl border border-red-400 p-2">
      ⚠️ ZERO requests selected for this file. Unlinking this file from all requests will delete the file.
    </div>
  );
};
